import { Outlet, NavLink } from 'react-router-dom'
import { LayoutDashboard, Calendar, Heart, User, MessageCircle, Menu, X, Globe, Star } from 'lucide-react'
import { useUIStore } from '@/store/uiStore'
import { UserMenu } from '@/components/shared/UserMenu'
import { MobileNav, type NavItem } from '@/components/layout/MobileNav'
import { ROUTES } from '@/constants/routes'
import { cn } from '@/lib/utils'
import { useUnreadMessages } from '@/hooks/useUnreadMessages'
import { webUrl } from '@/constants/site'

const baseNavItems: NavItem[] = [
  { label: 'Dashboard',        icon: LayoutDashboard, to: ROUTES.USER.DASHBOARD, end: true },
  { label: 'Le mie sessioni',  icon: Calendar,        to: ROUTES.USER.SESSIONS,  end: true },
  { label: 'Recensioni',       icon: Star,            to: ROUTES.USER.REVIEWS,   end: true },
  { label: 'Messaggi',         icon: MessageCircle,   to: ROUTES.USER.MESSAGES,  end: true },
  { label: 'Operatori salvati', icon: Heart,          to: ROUTES.USER.SAVED,     end: true },
  { label: 'Profilo',          icon: User,            to: ROUTES.USER.PROFILE,   end: true },
]

export function UserLayout() {
  const { sidebarOpen, toggleSidebar } = useUIStore()
  const unread = useUnreadMessages()
  const navItems: NavItem[] = baseNavItems.map((item) =>
    item.to === ROUTES.USER.MESSAGES ? { ...item, badge: unread } : item
  )

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-gray-50 md:flex-row">
      {/* Topbar + drawer mobile (< md) */}
      <MobileNav sections={[{ items: navItems }]} />

      <aside
        className={cn(
          'hidden flex-col border-r border-gray-200 bg-white transition-all duration-300 md:flex',
          sidebarOpen ? 'w-60' : 'w-16'
        )}
      >
        <div className="flex h-16 items-center justify-between border-b px-4">
          {sidebarOpen && (
            <a href={webUrl('/')} className="font-semibold text-holly-800 hover:text-holly-600">
              OLLYlife
            </a>
          )}
          <button
            onClick={toggleSidebar}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Link al sito pubblico */}
        <a
          href={webUrl('/')}
          title="Vai al sito pubblico"
          className={cn(
            'mx-2 mt-2 flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:border-holly-300 hover:text-holly-700',
            !sidebarOpen && 'justify-center px-2'
          )}
        >
          <Globe className="h-4 w-4 flex-shrink-0" />
          {sidebarOpen && <span>Vai al sito</span>}
        </a>

        <nav className="flex-1 space-y-1 p-2 py-4">
          {navItems.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              end
              className={({ isActive }) =>
                cn(
                  'relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-holly-50 text-holly-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                  !sidebarOpen && 'justify-center'
                )
              }
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {sidebarOpen && <span>{item.label}</span>}
              {item.badge && item.badge > 0 ? (
                <span className={cn(
                  'ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-holly-600 px-1.5 text-xs font-bold text-white',
                  !sidebarOpen && 'absolute right-1 top-1 ml-0'
                )}>
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div className={cn('border-t p-3', !sidebarOpen && 'flex justify-center')}>
          <UserMenu compact={!sidebarOpen} />
        </div>
      </aside>

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl px-4 py-4 sm:px-6 sm:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
